import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {SocialListTypeEnum} from './social-list-type.enum';
import {FollowerRestService} from '../../../api/services/follower.rest.service';
import {ReactionRestService} from '../../../api/services/reaction.rest.service';
import {QueryParamsApiModel} from '../../../api/models/query-params-api.model';
import {FilterParameterApiModel} from '../../../api/models/filter-parameter.api.model';
import {UserApiModel} from '../../../api/models/user.api-model';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import {ResultListApiModel} from '../../../api/models/result-list.api.model';
import {FollowApiModel, SubjectApiModel} from '../../../api/models/follow.api-model';
import {SocialGridItemInterface} from './social-grid-item.interface';
import {MediaObjectApiModel} from '../../../api/models/media-object.api-model';
import {MediaObjectRestService} from '../../../api/services/media-object.rest.service';
import {ReactionApiModel} from '../../../api/models/reaction.api.model';
import {SwiperItemInterface} from './swiper-items.interface';
import {FollowTypeEnum} from '../../../core/enums/follow-type.enum';
import {ReactionTypeEnum} from '../../../core/enums/reaction-type.enum';
import {plainToClass} from 'class-transformer';
import { HttpResponse } from '@angular/common/http';
import {ReactedWithEnum} from '../../../core/enums/reacted-with.enum';
import {UserViewModel} from '../../../features/user/settings/user.view-model';
import {UserRestService} from '../../../api/services/user.rest.service';
import {CurrentUserViewModel} from '../../../core/models/current-user.view-model';
import {ParticipantRestService} from '../../../api/services/participant.rest.service';
import {ParticipantApiModel} from '../../../api/models/participant.api.model';
import {EventRestService} from '../../../api/services/event.rest.service';
import {EventApiModel} from '../../../api/models/event.api.model';
import {KeyValue} from '@angular/common';
import {format} from 'date-fns';
import {EventParticipationRequestRestService} from '../../../api/services/event-participation-request.rest.service';
import {EventParticipationRequestApiModel} from '../../../api/models/event-participation-request.api-model';
import {EventParticipationRequestVehicleApiModel} from '../../../api/models/event-participation-request-vehicle.api-model';
import {ProjectRestService} from '../../../api/services/project.rest.service';
import {ProjectApiModel} from '../../../api/models/project.api-model';

@Injectable({
  providedIn: 'root'
})
export class SocialDataProviderService {

  public totalItems: {[key: string]: number} = {follows: 0, projects: 0, users: 0, events: 0, interested: 0, participate: 0, participated: 0, upcoming: 0, past: 0};
  public currentUserFollowersIds: string[] = [];
  public currentUserLikedIds: string[] = [];
  private currentUserFollowersLoadedFor: string | null = null;
  private currentUserLikesLoadedFor: string | null = null;
  constructor(
    private readonly followerRS: FollowerRestService,
    private readonly reactionRS: ReactionRestService,
    private readonly mediaRS: MediaObjectRestService,
    private readonly userRS: UserRestService,
    private readonly participantRS: ParticipantRestService,
    private readonly eventRS: EventRestService,
    private readonly eventParticipationRequestRS: EventParticipationRequestRestService,
    private readonly projectRS: ProjectRestService
  ) {
  }

  public getCollection(
    listTypeEnum: SocialListTypeEnum,
    item: KeyValue<string, SwiperItemInterface>,
    author: UserApiModel | null,
    event: EventApiModel = null,
    page: number,
    projectId?: number
  ): Observable<SocialGridItemInterface[]> {
    if (!listTypeEnum || !item?.key || !item?.value?.type) {
      return this.emptyCollection(item);
    }

    const queryParams = new QueryParamsApiModel();
    queryParams.filters.push(new FilterParameterApiModel('limit', 9, 'single'));
    queryParams.filters.push(new FilterParameterApiModel('discriminator', item.value.type, 'single'));
    queryParams.page = page;

    switch (listTypeEnum) {
      case SocialListTypeEnum.FOLLOWS:
        if (!author?.slug) {
          return this.emptyCollection(item);
        }

        queryParams.filters.push(new FilterParameterApiModel('followedBy.slug', author.slug, 'single'));
        return this.followerRS.getCollection(queryParams).pipe(mergeMap((resultListAM: ResultListApiModel<FollowApiModel>) => {
          item.value.count = resultListAM.total;
          this.totalItems[item.key] = resultListAM.total;
          const params = new QueryParamsApiModel();
          resultListAM.records.forEach((followAM: FollowApiModel) => {
            if (followAM.followed.avatar !== null) {
              params.filters.push(new FilterParameterApiModel('id', followAM.followed.avatar));
            }
          });
          return this.mediaRS.getMediaObjectCollection(params).pipe(map((resultListAM1: ResultListApiModel<MediaObjectApiModel>) => {
            return resultListAM.records.map((followAM: FollowApiModel) => {
              let avatar = {hyperlinks: []};
              if (followAM.followed.avatar !== null) {
                avatar = resultListAM1.records.find( e => e['@id'] === followAM.followed.avatar);
              }

              return {
                isPlaceholder: false,
                name: followAM.followed.identity,
                subject: followAM.followed,
                thumbnail: {name: 'thumbnail', hyperlinks: avatar.hyperlinks},
                previewUrl: this.generateLink(author, listTypeEnum, item.value.type, followAM.followed)
              };
            });
          }));
        }));
      case SocialListTypeEnum.REACTIONS:
        if (!author?.slug) {
          return this.emptyCollection(item);
        }

        return this.userRS.getLikedReactionsSubResourceCollection(author['@id'], queryParams).pipe(map((resultListAM: ResultListApiModel<ReactionApiModel>) => {
          item.value.count = resultListAM.total;
          this.totalItems[item.key] = resultListAM.total;
          return this.mapReactionGridItems(resultListAM.records || [], item, listTypeEnum, author);
        }));
      case SocialListTypeEnum.EVENTS:
        if (!author?.id) {
          return this.emptyCollection(item);
        }

        queryParams.filters.push(new FilterParameterApiModel('data.id', author.id, 'single'));
        queryParams.filters = queryParams.filters.filter(e => e.name !== 'discriminator');
        queryParams.filters.push(new FilterParameterApiModel('discriminator', 'PRIVATE', 'array'));
        queryParams.filters.push(new FilterParameterApiModel('state', item.value.type, 'single'));

        if (item.key === 'interested' || item.key === 'participate') {
          queryParams.filters.push(new FilterParameterApiModel('event.eventDates.end', format(new Date(), 'yyyy-MM-dd HH:mm:ss'), 'array_with_key', 'strictly_after'));

        }

        if (item.key === 'participated') {
          queryParams.filters.push(new FilterParameterApiModel('event.eventDates.end', format(new Date(), 'yyyy-MM-dd HH:mm:ss'), 'array_with_key', 'strictly_before'));
        }

        return this.participantRS.getCollection(queryParams).pipe(mergeMap((resultListAM: ResultListApiModel<ParticipantApiModel>) => {
          item.value.count = resultListAM.total;
          this.totalItems[item.key] = resultListAM.total;
          const params = new QueryParamsApiModel();
          resultListAM.records.forEach((participantAM: ParticipantApiModel) => {
              params.filters.push(new FilterParameterApiModel('slug', (participantAM.event as string).split('/').pop()));
          });

          if (params.filters.length === 0) {
            return of([]);
          }

          return this.eventRS.getCollection(params).pipe(map((resultListAM1: ResultListApiModel<EventApiModel>) => {
            return resultListAM1.records.map((eventAM: EventApiModel) => {
              const cover = eventAM.cover as MediaObjectApiModel | null;
              return {
                isPlaceholder: false,
                name: eventAM.identity,
                subject: eventAM,
                thumbnail: {name: 'thumbnail', hyperlinks: cover?.hyperlinks || [{name: 'thumbnail', uri: ''}]},
                previewUrl: this.generateLink(author, listTypeEnum, item.value.type, eventAM)
              };
            });
          }));
        }));
      case SocialListTypeEnum.EVENT_PARTICIPANTS:
        const eventId = event?.id || (typeof event?.['@id'] === 'string' ? event['@id'].split('/').pop() : null);
        if (!eventId) {
          return this.emptyCollection(item);
        }

        queryParams.filters.push(new FilterParameterApiModel('event.id', eventId, 'single'));
        queryParams.filters.push(new FilterParameterApiModel('state', item.value.type, 'single'));

        if (item.key === 'interested' || item.key === 'participate') {
          queryParams.filters.push(new FilterParameterApiModel('event.eventDates.end', format(new Date(), 'yyyy-MM-dd HH:mm:ss'), 'array_with_key', 'strictly_after'));

        }

        if (item.key === 'participated') {
          queryParams.filters.push(new FilterParameterApiModel('event.eventDates.end', format(new Date(), 'yyyy-MM-dd HH:mm:ss'), 'array_with_key', 'strictly_before'));
        }

        return this.participantRS.getCollection(queryParams).pipe(mergeMap((resultListAM: ResultListApiModel<ParticipantApiModel>) => {
          const participants = (resultListAM.records || []).filter((participantAM: ParticipantApiModel) => !this.isDeletedUserSubject(participantAM?.data));
          item.value.count = participants.length;
          this.totalItems[item.key] = participants.length;
          const eventIdFilter = event?.id || eventId;

          if (!participants.length || !eventIdFilter) {
            return of(participants.map((participantAM: ParticipantApiModel) => {
              const subject = participantAM.data as SubjectApiModel;
              const avatar = subject?.avatar as MediaObjectApiModel | null;
              return {
                isPlaceholder: false,
                name: subject.identity,
                subject: participantAM,
                thumbnail: {name: 'thumbnail', hyperlinks: avatar?.hyperlinks || [{name: 'thumbnail', uri: ''}]},
                previewUrl: this.generateLink(author, listTypeEnum, item.value.type, participantAM.data),
                vehicleThumbnails: []
              };
            }));
          }

          const requestsByParticipant$ = participants.map((participantAM: ParticipantApiModel) => {
            const userSlug = this.extractUserSlug(participantAM?.data);
            if (!userSlug) {
              return of({participantAM, requests: [] as EventParticipationRequestApiModel[]});
            }

            const requestQueryParams = new QueryParamsApiModel([
              new FilterParameterApiModel('declaration.event.id', eventIdFilter, 'single'),
              new FilterParameterApiModel('declaration.declaredByUser.slug', userSlug, 'single'),
              new FilterParameterApiModel('status', 'APPROVED', 'single'),
            ]);
            requestQueryParams.limit = 5;

            return this.eventParticipationRequestRS.getCollection(requestQueryParams).pipe(
              map((requestResultList: ResultListApiModel<EventParticipationRequestApiModel>) => ({
                participantAM,
                requests: requestResultList?.records || []
              })),
              catchError(() => of({participantAM, requests: [] as EventParticipationRequestApiModel[]}))
            );
          });

          return forkJoin(requestsByParticipant$).pipe(map((perParticipantRequests) => {
            return perParticipantRequests;
          }), mergeMap((perParticipantRequests) => {
            const allVehicleSlugs = perParticipantRequests
              .flatMap(({requests}) => requests)
              .flatMap((request) => request?.vehicles || [])
              .map((vehicle: EventParticipationRequestVehicleApiModel) => this.extractProjectSlugFromVehicle(vehicle))
              .filter((slug): slug is string => !!slug);

            return this.fetchProjectThumbsBySlugs(allVehicleSlugs).pipe(map((projectThumbMap) => {
              return perParticipantRequests.map(({participantAM, requests}) => {
                const subject = participantAM.data as SubjectApiModel;
                const avatar = subject?.avatar as MediaObjectApiModel | null;
                const participantSlug = this.extractUserSlug(participantAM?.data);
                const vehicleThumbnails = requests
                  .flatMap((request) => request?.vehicles || [])
                  .map((vehicle: EventParticipationRequestVehicleApiModel) => this.mapVehicleThumbItem(vehicle, projectThumbMap, participantSlug))
                  .filter((thumb) => !!thumb) as Array<{thumb: any; previewUrl?: string | null}>;

                return {
                  isPlaceholder: false,
                  name: subject.identity,
                  subject: participantAM,
                  thumbnail: {name: 'thumbnail', hyperlinks: avatar?.hyperlinks || [{name: 'thumbnail', uri: ''}]},
                  previewUrl: this.generateLink(author, listTypeEnum, item.value.type, participantAM.data),
                  vehicleThumbnails
                };
              });
            }));
          }));
        }), map((items: SocialGridItemInterface[]) => {
          return items;
        }));
      case SocialListTypeEnum.PROJECT_EVENTS:
        if (!projectId) {
          return this.emptyCollection(item);
        }

        const requestsQueryParams = new QueryParamsApiModel();
        requestsQueryParams.pagination = false;
        requestsQueryParams.filters.push(new FilterParameterApiModel('vehicles.project.id', projectId, 'single'));
        requestsQueryParams.filters.push(new FilterParameterApiModel('status', 'APPROVED', 'single'));

        return this.eventParticipationRequestRS.getCollection(requestsQueryParams).pipe(mergeMap((requestsResultList: ResultListApiModel<EventParticipationRequestApiModel>) => {
          const eventSlugs = new Set<string>();
          (requestsResultList.records || []).forEach((request) => {
            const declaration = request?.declaration as any;
            const slug = this.extractEventSlug(declaration?.event);
            if (slug) {
              eventSlugs.add(slug);
            }
          });

          if (!eventSlugs.size) {
            item.value.count = 0;
            this.totalItems[item.key] = 0;
            return of([]);
          }

          const eventsQueryParams = new QueryParamsApiModel();
          eventsQueryParams.page = page;
          eventsQueryParams.limit = 9;
          Array.from(eventSlugs).forEach((slug) => {
            eventsQueryParams.filters.push(new FilterParameterApiModel('slug', slug, 'array'));
          });

          const now = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
          if (item.key === 'upcoming') {
            eventsQueryParams.filters.push(new FilterParameterApiModel('eventDates.end', now, 'array_with_key', 'strictly_after'));
          } else {
            eventsQueryParams.filters.push(new FilterParameterApiModel('eventDates.end', now, 'array_with_key', 'strictly_before'));
          }

          return this.eventRS.getCollection(eventsQueryParams).pipe(map((eventsResultList: ResultListApiModel<EventApiModel>) => {
            item.value.count = eventsResultList.total;
            this.totalItems[item.key] = eventsResultList.total;
            return (eventsResultList.records || []).map((eventAM: EventApiModel) => {
              const cover = eventAM.cover as MediaObjectApiModel | null;
              return {
                isPlaceholder: false,
                name: eventAM.identity,
                subject: eventAM,
                thumbnail: {name: 'thumbnail', hyperlinks: cover?.hyperlinks || [{name: 'thumbnail', uri: ''}]},
                previewUrl: this.generateLink(author, listTypeEnum, item.value.type, eventAM)
              };
            });
          }));
        }));
    }
  }

  private emptyCollection(item: KeyValue<string, SwiperItemInterface>): Observable<SocialGridItemInterface[]> {
    if (item?.value) {
      item.value.count = 0;
    }

    if (item?.key) {
      this.totalItems[item.key] = 0;
    }

    return of([]);
  }

  private mapReactionGridItems(
    reactions: ReactionApiModel[],
    item: KeyValue<string, SwiperItemInterface>,
    listTypeEnum: SocialListTypeEnum,
    author: UserApiModel | null
  ): SocialGridItemInterface[] {
    return reactions
      .map((reactionAM) => {
        if (!reactionAM?.subject || typeof reactionAM.subject === 'string') {
          return null;
        }

        return {
          isPlaceholder: false,
          name: this.resolveReactionSubjectName(reactionAM.subject, item),
          subject: reactionAM.subject,
          thumbnail: this.resolveReactionThumbnail(reactionAM.subject, item.value.type),
          previewUrl: this.generateLink(author, listTypeEnum, item.value.type, reactionAM.subject)
        };
      })
      .filter((entry) => this.isDefined(entry));
  }

  private resolveReactionSubjectName(subject: SubjectApiModel, item: KeyValue<string, SwiperItemInterface>): string {
    const reactionSubject = subject as SubjectApiModel & {name?: string; alias?: string};
    return reactionSubject.identity || reactionSubject.name || reactionSubject.alias || reactionSubject.slug || item.value.label || item.value.type;
  }

  private resolveReactionThumbnail(subject: SubjectApiModel, reactionType: string): {name: string; hyperlinks: {name: string; uri: string}[]} {
    const mediaObject = this.resolveReactionMediaObject(subject, reactionType);
    return {
      name: 'thumbnail',
      hyperlinks: mediaObject?.hyperlinks || [{name: 'thumbnail', uri: ''}]
    };
  }

  private resolveReactionMediaObject(subject: SubjectApiModel, reactionType: string): MediaObjectApiModel | null {
    const reactionSubject = subject as SubjectApiModel & {
      thumbnail?: MediaObjectApiModel | string | null;
      cover?: MediaObjectApiModel | string | null;
    };
    let mediaObject: MediaObjectApiModel | string | null = null;

    switch (reactionType) {
      case ReactionTypeEnum.USER_REACTION:
        mediaObject = reactionSubject.avatar as MediaObjectApiModel | string | null;
        break;
      case ReactionTypeEnum.PROJECT_REACTION:
        mediaObject = reactionSubject.thumbnail || (reactionSubject.theme as MediaObjectApiModel | string | null);
        break;
      case ReactionTypeEnum.EVENT_REACTION:
        mediaObject = reactionSubject.cover || null;
        break;
      default:
        mediaObject = (reactionSubject.avatar as MediaObjectApiModel | string | null)
          || reactionSubject.thumbnail
          || reactionSubject.cover
          || (reactionSubject.theme as MediaObjectApiModel | string | null);
    }

    if (!mediaObject || typeof mediaObject === 'string') {
      return null;
    }

    return Array.isArray(mediaObject.hyperlinks) ? mediaObject : null;
  }

  private isDefined<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
  }

  private extractVehicleThumb(vehicle: EventParticipationRequestVehicleApiModel): any | null {
    const project: any = vehicle?.project;
    if (!project || typeof project === 'string') {
      return null;
    }

    return project?.thumbnail || project?.theme || null;
  }

  private extractUserIri(user: any): string | null {
    return this.extractResourceIri(user);
  }

  private extractUserId(user: any): number | null {
    if (!user) {
      return null;
    }

    if (typeof user === 'object' && typeof user.id === 'number') {
      return user.id;
    }

    const iri = this.extractUserIri(user);
    if (!iri) {
      return null;
    }

    const idPart = iri.split('/').pop();
    const parsed = idPart ? Number(idPart) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }

  private isDeletedUserSubject(subject: any): boolean {
    return !!subject && typeof subject === 'object' && subject.deleted === true;
  }

  private extractUserSlug(user: any): string | null {
    if (!user) {
      return null;
    }

    if (typeof user === 'object' && typeof user.slug === 'string' && user.slug.length) {
      return user.slug;
    }

    const iri = this.extractUserIri(user);
    if (!iri) {
      return null;
    }

    const slug = iri.split('/').pop();
    return slug || null;
  }

  private extractProjectSlugFromVehicle(vehicle: EventParticipationRequestVehicleApiModel): string | null {
    const project: any = vehicle?.project;
    if (project && typeof project === 'object' && typeof project.slug === 'string' && project.slug.length) {
      return project.slug;
    }

    if (typeof project === 'string') {
      const slug = project.split('/').pop();
      return slug || vehicle?.projectSlugSnapshot || null;
    }

    return vehicle?.projectSlugSnapshot || null;
  }

  private fetchProjectThumbsBySlugs(slugs: string[]): Observable<{[slug: string]: any}> {
    const uniqueSlugs = Array.from(new Set(slugs.filter((slug) => !!slug)));
    if (!uniqueSlugs.length) {
      return of({});
    }

    const query = new QueryParamsApiModel(uniqueSlugs.map((slug) => new FilterParameterApiModel('slug', slug, 'array')));
    query.pagination = false;

    return this.projectRS.getCollection(query).pipe(
      mergeMap((resultList: ResultListApiModel<ProjectApiModel>) => {
        const mapBySlug: {[slug: string]: any} = {};
        const mediaRefBySlug: {[slug: string]: string} = {};

        (resultList?.records || []).forEach((project) => {
          if (!project?.slug) {
            return;
          }

          const ref = (project as any).thumbnail || (project as any).theme || null;
          if (!ref) {
            return;
          }

          if (typeof ref === 'object' && ref?.hyperlinks?.length) {
            mapBySlug[project.slug] = ref;
            return;
          }

          if (typeof ref === 'string') {
            mediaRefBySlug[project.slug] = ref;
          }
        });

        const mediaRefs = Array.from(new Set(Object.values(mediaRefBySlug)));
        if (!mediaRefs.length) {
          return of(mapBySlug);
        }

        const mediaParams = new QueryParamsApiModel();
        mediaParams.pagination = false;
        mediaRefs.forEach((ref) => mediaParams.filters.push(new FilterParameterApiModel('id', ref, 'array')));

        return this.mediaRS.getMediaObjectCollection(mediaParams).pipe(
          map((mediaResult: ResultListApiModel<MediaObjectApiModel>) => {
            const mediaByIri: {[iri: string]: MediaObjectApiModel} = {};
            (mediaResult?.records || []).forEach((media) => {
              if (media?.['@id']) {
                mediaByIri[media['@id']] = media;
              }
            });

            Object.entries(mediaRefBySlug).forEach(([slug, ref]) => {
              if (mediaByIri[ref]) {
                mapBySlug[slug] = mediaByIri[ref];
              }
            });

            return mapBySlug;
          }),
          catchError(() => of(mapBySlug))
        );
      }),
      catchError(() => of({}))
    );
  }

  private mapVehicleThumbItem(
    vehicle: EventParticipationRequestVehicleApiModel,
    projectThumbMap: {[slug: string]: any},
    participantSlug: string | null
  ): {thumb: any; previewUrl?: string | null} | null {
    const project: any = vehicle?.project;
    const projectSlug = this.extractProjectSlugFromVehicle(vehicle);
    const thumb = this.extractVehicleThumb(vehicle) || (projectSlug ? projectThumbMap[projectSlug] || null : null);
    if (!thumb) {
      return null;
    }

    const ownerSlug = this.extractProjectOwnerSlug(project) || participantSlug;
    const previewUrl = ownerSlug && projectSlug ? `/${ownerSlug}/garage/${projectSlug}` : null;

    return {thumb, previewUrl};
  }

  private extractProjectOwnerSlug(project: any): string | null {
    if (!project || typeof project === 'string') {
      return null;
    }

    const user = project?.user;
    if (!user) {
      return null;
    }

    if (typeof user === 'object' && typeof user.slug === 'string' && user.slug.length) {
      return user.slug;
    }

    if (typeof user === 'string') {
      const slug = user.split('/').pop();
      return slug || null;
    }

    return null;
  }

  private extractEventSlug(eventRef: EventApiModel | string | null | undefined): string | null {
    if (!eventRef) {
      return null;
    }

    if (typeof eventRef === 'string') {
      const parts = eventRef.split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] : null;
    }

    if (eventRef.slug) {
      return eventRef.slug;
    }

    if (eventRef['@id']) {
      const parts = String(eventRef['@id']).split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] : null;
    }

    return null;
  }

  public postFollow(userVM: UserViewModel): Observable<UserViewModel> {
    const userAM = userVM.toApiModel();
    return this.followerRS.create(plainToClass(FollowApiModel, {followed: userAM['@id'], type: FollowTypeEnum.USER}))
      .pipe(map((followAM: FollowApiModel) => {
        userVM.receivedFollows.unshift(followAM);
        return userVM;
      }));
  }

  public removeFollow(followAM: FollowApiModel, userVM: UserViewModel): Observable<UserViewModel> {
    return this.followerRS.delete(followAM).pipe(map((e: HttpResponse<any>) => {
      userVM.receivedFollows = userVM.receivedFollows.filter(x => x.followedBy !== followAM.followedBy);
      return userVM;
    }));
  }

  public postReaction(userVM: UserViewModel): Observable<UserViewModel> {
    const userAM = userVM.toApiModel();
    return this.reactionRS.create(plainToClass(ReactionApiModel, {reactedWith: ReactedWithEnum.THUMBS_UP, subject: userAM['@id'], type: ReactionTypeEnum.USER_REACTION}))
      .pipe(map((reactionAM: ReactionApiModel) => {
        userVM.receivedReactions.unshift(reactionAM);
        return userVM;
      }));
  }

  public removeReaction(reactionAM: ReactionApiModel, userVM: UserViewModel): Observable<UserViewModel> {
    return this.reactionRS.delete(reactionAM).pipe(map((e: HttpResponse<any>) => {
      userVM.receivedReactions = userVM.receivedReactions.filter(x => x.author !== reactionAM.author);
      return userVM;
    }));
  }

  public getAllFollowedCollection(userVM: UserViewModel): Observable<FollowApiModel[]> {
    const queryParams = new QueryParamsApiModel();
    // queryParams.filters.push(new FilterParameterApiModel('limit', 100, 'single'));
    queryParams.filters.push(new FilterParameterApiModel('followed.slug', userVM.slug, 'array'));
    queryParams.filters.push(new FilterParameterApiModel('followedBy.slug', userVM.slug, 'array_with_key', 'neq'));
    queryParams.filters.push(new FilterParameterApiModel('discriminator', FollowTypeEnum.USER, 'array'));
    queryParams.filters.push(new FilterParameterApiModel('discriminator', FollowTypeEnum.PROJECT, 'array'));
    userVM.getApiModel().projects.forEach((followedId: string) => {
      const arr = followedId.split('/');
      queryParams.filters.push(new FilterParameterApiModel('followed.slug', arr[arr.length - 1], 'array'));
    });
    return this.followerRS.getCollection(queryParams).pipe(map((resultListAM: ResultListApiModel<FollowApiModel>) => {
      return resultListAM.records;
    }));
  }

  public getAllLikesCollection(userVM: UserViewModel): Observable<ReactionApiModel[]> {
    const queryParams = new QueryParamsApiModel();
    queryParams.filters.push(new FilterParameterApiModel('user.slug', userVM.slug, 'array'));
    queryParams.filters.push(new FilterParameterApiModel('author.slug', userVM.slug, 'array_with_key', 'neq'));
    queryParams.filters.push(new FilterParameterApiModel('discriminator', ReactionTypeEnum.USER_REACTION, 'array'));
    queryParams.filters.push(new FilterParameterApiModel('discriminator', ReactionTypeEnum.PROJECT_REACTION, 'array'));
    userVM.getApiModel().projects.forEach((followedId: string) => {
      const arr = followedId.split('/');
      queryParams.filters.push(new FilterParameterApiModel('project.slug', arr[arr.length - 1], 'array'));
    });
    return this.reactionRS.getCollection(queryParams).pipe(map((resultListAM: ResultListApiModel<ReactionApiModel>) => {
      return resultListAM.records;
    }));
  }

  public getAllUserFollowsCollection(userVM: UserViewModel): Observable<FollowApiModel[]> {
    const params = new QueryParamsApiModel();
    params.pagination = false;
    return this.userRS.getReceivedFollowsSubResourceCollection(userVM['@id'], params).pipe(map((resultListAM: ResultListApiModel<FollowApiModel>) => {
      userVM.receivedFollows = resultListAM.records;
      return resultListAM.records;
    }));
  }

  public getAllUserLikesCollection(userVM: UserViewModel): Observable<ReactionApiModel[]> {
    const params = new QueryParamsApiModel();
    params.pagination = false;
    return this.userRS.getReceivedLikesSubResourceCollection(userVM['@id'], params).pipe(map((resultListAM: ResultListApiModel<ReactionApiModel>) => {
      userVM.receivedReactions = resultListAM.records;
      return resultListAM.records;
    }));
  }

  public getAllUserAggregatedLikesCollection(userVM: UserViewModel): Observable<ReactionApiModel[]> {
    const params = new QueryParamsApiModel();
    params.pagination = false;
    return this.userRS.getAggregatedReceivedLikesSubResourceCollection(userVM['@id'], params).pipe(map((resultListAM: ResultListApiModel<ReactionApiModel>) => {
      userVM.receivedReactions = resultListAM.records;
      return resultListAM.records;
    }));
  }

  public fetchCurrentUserFollows(currentUserVM: CurrentUserViewModel): Observable<ResultListApiModel<FollowApiModel>> {
    if (this.currentUserFollowersLoadedFor === currentUserVM['@id'] && this.currentUserFollowersIds) {
      const resultListAM = new ResultListApiModel<FollowApiModel>(FollowApiModel);
      resultListAM.records = this.currentUserFollowersIds.map((followedId: string) => ({
        followed: {'@id': followedId}
      } as FollowApiModel));
      resultListAM.total = resultListAM.records.length;
      resultListAM.limit = resultListAM.records.length;
      return of(resultListAM);
    }

    const params = new QueryParamsApiModel();
    params.pagination = false;
    return this.userRS.getFollowsSubResourceCollection(currentUserVM['@id'], params).pipe(tap((resultListAM: ResultListApiModel<FollowApiModel>) => {
      this.currentUserFollowersIds = resultListAM.records.map(e => e.followed['@id']);
      this.currentUserFollowersLoadedFor = currentUserVM['@id'];
    }));
  }

  public fetchCurrentUserLikes(currentUserVM: CurrentUserViewModel): Observable<ResultListApiModel<ReactionApiModel>> {
    if (this.currentUserLikesLoadedFor === currentUserVM['@id'] && this.currentUserLikedIds) {
      const resultListAM = new ResultListApiModel<ReactionApiModel>(ReactionApiModel);
      resultListAM.records = this.currentUserLikedIds.map((subjectId: string) => ({
        subject: {'@id': subjectId}
      } as ReactionApiModel));
      resultListAM.total = resultListAM.records.length;
      resultListAM.limit = resultListAM.records.length;
      return of(resultListAM);
    }

    const params = new QueryParamsApiModel();
    params.pagination = false;
    return this.userRS.getReactionsSubResourceCollection(currentUserVM['@id'], params).pipe(tap((resultListAM: ResultListApiModel<ReactionApiModel>) => {
      this.currentUserLikedIds = resultListAM.records
        .map((reactionAM) => this.extractSubjectIri(reactionAM.subject))
        .filter((subjectIri): subjectIri is string => !!subjectIri);
      this.currentUserLikesLoadedFor = currentUserVM['@id'];
    }));
  }

  public isFollowedByCurrentUser(subjectUser: UserViewModel): boolean {
    const subjectId = subjectUser?.['@id'];
    return !!subjectId && this.currentUserFollowersIds.includes(subjectId);
  }

  public isLikedByCurrentUser(subjectUser: UserViewModel): boolean {
    const subjectId = subjectUser?.['@id'];
    return !!subjectId && this.currentUserLikedIds.includes(subjectId);
  }

  private extractSubjectIri(subject: string | SubjectApiModel | null | undefined): string | null {
    return this.extractResourceIri(subject);
  }

  private extractResourceIri(reference: any): string | null {
    if (!reference) {
      return null;
    }

    if (typeof reference === 'string') {
      return reference;
    }

    if (typeof reference === 'object' && typeof reference['@id'] === 'string') {
      return reference['@id'];
    }

    return null;
  }

  private generateLink(user: UserApiModel | null, listType: SocialListTypeEnum, type: string, item: any): string | null {
    const projectSlug = item?.slug;
    const ownerSlug = item?.user?.slug || null;

    switch (listType) {
      case SocialListTypeEnum.FOLLOWS:
        switch (type) {
          case FollowTypeEnum.PROJECT: return projectSlug ? (ownerSlug ? `/${ownerSlug}/garage/${projectSlug}` : `/garage/${projectSlug}`) : null;
          case FollowTypeEnum.USER: return `/${item.slug}/profile`;
          case FollowTypeEnum.EVENT: return `/events/${item.slug}/general`;
        }
        break;
      case SocialListTypeEnum.REACTIONS:
        switch (type) {
          case ReactionTypeEnum.PROJECT_REACTION: return projectSlug ? (ownerSlug ? `/${ownerSlug}/garage/${projectSlug}` : `/garage/${projectSlug}`) : null;
          case ReactionTypeEnum.USER_REACTION: return `/${item.slug}/profile`;
          case ReactionTypeEnum.EVENT_REACTION: return `/events/${item.slug}/general`;
        }
        break;
      case SocialListTypeEnum.EVENTS:
        return `/events/${item.slug}`;
      case SocialListTypeEnum.EVENT_PARTICIPANTS:
        return `/${item.slug}/profile`;
      case SocialListTypeEnum.PROJECT_EVENTS:
        return `/events/${item.slug}`;
    }
  }
}
